import { FC, useCallback, useEffect, useState } from 'react'
import { Dialog, TextField } from '../atoms'
import { useAppDispatch } from '../../lib/store'
import { Button, Stack, Typography } from '@mui/material'
import { toast } from '../../lib/utils'
import { InfoUsageDisclaimer } from './InfoUsageDisclaimer'
import { saveProfileChangesThunk } from '../../lib/slices'
import { IoAlertCircle, IoCheckmarkCircle } from 'react-icons/io5'
import {
    errorColor,
    primaryIconSize,
    successColor,
} from '../../lib/styles/universal'
import { isPostalCode } from 'validator'

interface ZipCodeAlertProps {
    message?: string
    onSubmit?(): void
    open: boolean
    onClose(): void
}

export const ZipCodeAlert: FC<ZipCodeAlertProps> = ({
    message = 'We recently made some updates to our account system. Please confirm your ZIP code to ensure your information is up-to-date.',
    onSubmit,
    open,
    onClose,
}) => {
    const dispatch = useAppDispatch()
    const [zip, setZip] = useState('')
    const [error, setError] = useState<string>()

    const handleZipChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const value = e.target.value
            setZip(value)
            setError(undefined)
        },
        []
    )

    const submitZipCode = useCallback(() => {
        if (!isPostalCode(zip, 'any')) {
            setError('Invalid ZIP or postal code.')
            return
        }
        dispatch(saveProfileChangesThunk({ zip }))
            .then(() => {
                toast(
                    'Thanks!',
                    <IoCheckmarkCircle
                        color={successColor}
                        size={primaryIconSize}
                    />
                )
                onClose()
                onSubmit?.()
            })
            .catch(() => {
                toast(
                    'We were unable to save your ZIP code. Please try again.',
                    <IoAlertCircle color={errorColor} size={primaryIconSize} />
                )
            })
    }, [zip, dispatch, onSubmit, onClose])

    useEffect(() => {
        if (open) {
            setZip('')
            setError(undefined)
        }
    }, [open])

    return (
        <Dialog
            title="Confirm ZIP Code"
            content={
                <Stack gap="1rem">
                    <Typography>{message}</Typography>
                    <Stack gap="0.5rem">
                        <TextField
                            value={zip}
                            onChange={handleZipChange}
                            placeholder="Enter your ZIP code"
                            fullWidth
                            type="text"
                            variant="outlined"
                            error={!!error}
                        />
                        {error && (
                            <Typography color="error">{error}</Typography>
                        )}
                    </Stack>
                    <InfoUsageDisclaimer />
                </Stack>
            }
            open={open}
            onClose={onClose}
            ctas={[
                <Button
                    key="confirm"
                    variant="contained"
                    onClick={submitZipCode}
                    disabled={!zip}
                >
                    Submit
                </Button>,
            ]}
        />
    )
}
